import React from "react";
import styled from "styled-components";
import Chart from 'react-apexcharts';
import { ApexOptions } from 'apexcharts';
import {
    Card as MuiCard,
    CardContent as MuiCardContent,
    CardHeader,
    useTheme,
    Box
} from "@mui/material";
import { spacing } from "@mui/system";
import { chartColors } from "../config/analyticsCharts";
import { getCategoryColor } from "../utils/categoryColors";
import { t } from "../../../../utils/translate";

const defaultColors = chartColors.seriesColors;

const Card = styled(MuiCard)(spacing);
const CardContent = styled(MuiCardContent)`
  &:last-child {
    padding-bottom: ${(props) => props.theme.spacing(4)};
  }
`;

interface BarChartProps {
    series: {
        name: string,
        data: number[]
    }[],
    title: React.ReactNode,
    categories: string[],
    type?: 'bar' | 'line' | 'area' | 'radar' | 'scatter' | 'heatmap' | 'pie' | 'donut',
    stacked?: boolean,
    horizontal?: boolean
    total?: number,
    ditributed?: boolean,
    patientIds?: Record<string, number[]>
}

const EnhancedChart = ({ 
    series, 
    title, 
    categories, 
    type = 'bar',
    stacked = false,
    horizontal = false,
    total,
    ditributed = false,
    patientIds
}: BarChartProps) => {
    const theme = useTheme();

    // If we have patient IDs, we need to calculate gender-based data
    React.useEffect(() => {
        if (patientIds && (type === 'bar' || type === 'line') && series && Array.isArray(series) && series.length === 3) {
            // This is our diagnosis chart with Female, Male, Total series
            // We'd need to fetch patient data here and update the series data
            // For now, this will be handled in the parent component
        }
    }, [patientIds, series, type]);

    // Function to sum data and get labels for pie/donut
    const getSummedSeriesDataForPie = (series: { name: string, data: number[] }[] | undefined, categories?: string[]) => {
        if (!series || !Array.isArray(series) || series.length === 0) {
            return { values: [], labels: [] };
        }
        if (categories && series.length === 1) {
            // Handle single series with categories (like your insurance data)
            return {
                values: series[0]?.data || [],
                labels: categories || []
            };
        } else {
            // Handle multiple series (original behavior)
            const values = series.map((s) => (s?.data || []).reduce((a, b) => a + b, 0));
            const labels = series.map((s) => s?.name || '');
            return { values, labels };
        }
    };

    const isPieOrDonut = ['pie', 'donut'].includes(type);

    // Process data and determine labels/colors based on chart type
    const { chartSeries, chartLabels, finalColors } = React.useMemo(() => {
        let processedSeries: ApexAxisChartSeries | ApexNonAxisChartSeries = [];
        let processedLabels: string[] | undefined = undefined;
        let calculatedColors: string[] = [...defaultColors]; // Start with default

        if (isPieOrDonut) {
            if (!series || !Array.isArray(series)) {
                processedSeries = [];
                processedLabels = [];
            } else {
                const data = getSummedSeriesDataForPie(series, categories);
                if (!data || !data.labels || !Array.isArray(data.labels) || !data.values || !Array.isArray(data.values)) {
                    processedSeries = [];
                    processedLabels = [];
                } else {
                const sorted = data.labels
                    .map((label, index) => ({ 
                        label: (label != null ? String(label) : '') || '', 
                        value: (data.values && data.values[index] != null) ? data.values[index] : 0 
                    }))
                    .filter(item => item != null)
                    .sort((a, b) => {
                        const labelA = (a?.label != null ? String(a.label) : '') || '';
                        const labelB = (b?.label != null ? String(b.label) : '') || '';
                        return labelA.localeCompare(labelB);
                    });

                    processedSeries = sorted.map(item => item?.value ?? 0); // number[] for pie/donut series
                    processedLabels = sorted.map(item => item?.label ?? ''); // string[] for pie/donut labels
                }
            }
            
            // Calculate colors based on category names
            calculatedColors = processedLabels ? processedLabels.map(label => 
                getCategoryColor(label)
            ) : [];
        } else {
            // For other charts, sort series by name
            if (!series || !Array.isArray(series)) {
                processedSeries = [];
                calculatedColors = [];
            } else {
                const sortedSeries = series.map(s => ({
                    name: s?.name || '',
                    data: s?.data || []
                })).filter(s => s != null).sort((a, b) => {
                    const nameA = (a?.name != null ? String(a.name) : '') || '';
                    const nameB = (b?.name != null ? String(b.name) : '') || '';
                    return nameA.localeCompare(nameB);
                });
                
                processedSeries = sortedSeries; // { name: string; data: number[] }[] for others
                
                // Calculate colors based on category names (series names)
                calculatedColors = sortedSeries.map(s => 
                    getCategoryColor(s.name)
                );
            }
        }

        return { chartSeries: processedSeries, chartLabels: processedLabels, finalColors: calculatedColors };
    }, [series, categories, isPieOrDonut]);

    const chartOptions: ApexOptions = React.useMemo(() => ({
        chart: {
            background: 'transparent',
            stacked: stacked,
            toolbar: {
                show: true,
                tools: {
                    download: true,
                    selection: true,
                    zoom: true,
                    zoomin: true,
                    zoomout: true,
                    pan: true,
                }
            },
            zoom: {
                enabled: true
            }
        },
        plotOptions: {
            bar: {
                horizontal: horizontal,
                columnWidth: '80%',
                borderRadius: 4,
                distributed: ditributed,
                enableShades: true,
                shadeIntensity: 0.5,
                reverseNegativeShade: true,
                reversePositiveShade: true,
            },
        },
        colors: finalColors,
        dataLabels: {
            enabled: true,
            formatter: function(val: any) {
                if (typeof val === 'number') {
                    // For pie/donut charts, values are percentages - format to 1 decimal place
                    if (type === 'pie' || type === 'donut') {
                        return val.toFixed(1) + '%';
                    }
                    // For other charts, format to 0-2 decimal places based on value
                    if (val % 1 === 0) {
                        // Whole number - no decimals
                        return val.toString();
                    } else if (val < 1) {
                        // Small decimal - 2 decimal places
                        return val.toFixed(2);
                    } else {
                        // Regular number - 1 decimal place
                        return val.toFixed(1);
                    }
                }
                return String(val);
            }
        },
        fill: {
            type: type === 'area' ? 'gradient' : 'solid',
            opacity: type === 'area' ? [0.35, 0.25, 0.2] : 1,
        },
        grid: {
            show: true,
            borderColor: theme.palette.divider,
            strokeDashArray: 3,
            position: 'back' as const,
        },
        legend: {
            position: 'bottom',
            horizontalAlign: 'center',
            floating: false,
            offsetY: 0,
            offsetX: 0,
            fontSize: '16px',
            labels: {
                colors: theme.palette.text.secondary,
                useSeriesColors: false
            },
            markers: {
                size: 8,
                strokeWidth: 0,
                shape: 'circle' as const
            }
        },
        stroke: {
            curve: 'straight',
            width: type === 'bar' ? 0 : 2,
        },
        theme: {
            mode: theme.palette.mode
        },
        tooltip: {
            shared: true,
            intersect: false,
            theme: theme.palette.mode,
            y: {
                formatter: function(value: any) {
                    if (typeof value === 'number') {
                        // For pie/donut charts, values are percentages - format to 1 decimal place
                        if (type === 'pie' || type === 'donut') {
                            return value.toFixed(1) + '%';
                        }
                        // For other charts, format to 0-2 decimal places based on value
                        if (value % 1 === 0) {
                            // Whole number - no decimals
                            return value.toString();
                        } else if (value < 1) {
                            // Small decimal - 2 decimal places
                            return value.toFixed(2);
                        } else {
                            // Regular number - 1 decimal place
                            return value.toFixed(1);
                        }
                    }
                    return String(value);
                }
            }
        },
        xaxis: {
            type: 'category',
            categories: categories,
            axisBorder: {
                show: true,
                color: theme.palette.divider,
            },
            axisTicks: {
                show: true,
                color: theme.palette.divider,
            },
            labels: {
                style: {
                    colors: theme.palette.text.secondary,
                    fontSize: '14px'
                },
            }
        },
        yaxis: {
            labels: {
                style: {
                    colors: theme.palette.text.secondary,
                    fontSize: '14px'
                },
                formatter: function(value: any) {
                    if (typeof value === 'number') {
                        // Format y-axis labels with appropriate decimal places
                        if (value % 1 === 0) {
                            return value.toString();
                        } else if (value < 1) {
                            return value.toFixed(2);
                        } else {
                            return value.toFixed(1);
                        }
                    }
                    return String(value);
                }
            }
        },
        // Special settings for pie/donut charts
        ...(isPieOrDonut && {
            labels: chartLabels,
            pie: {
                expandOnClick: true,
                donut: {
                    size: '70%',
                    labels: {
                        show: true,
                        name: {
                            show: true
                        },
                        value: {
                            show: false // Changed from true to false to hide individual values
                        },
                        total: {
                            show: false, // Changed from true to false to hide the center value
                            label: t('general.total'), // Use the translation from global.json
                            formatter: function (w: any) {
                                return w.globals.seriesTotals.reduce((a: number, b: number) => a + b, 0);
                            }
                        }
                    },
                }
            }
        })
    }), [stacked, horizontal, ditributed, theme, categories, chartLabels, finalColors, isPieOrDonut]);

    return (
        <Card>
            <CardHeader 
                title={title}
                subheader={total && total > 1 ? `Total: ${total}` : null} 
            />
            <CardContent>
                <Box>
                    <Chart
                        options={chartOptions}
                        series={chartSeries}
                        type={type}
                        height={400}
                    />
                </Box>
            </CardContent>
        </Card>
    );
};


export default EnhancedChart;
